import React from 'react';

import Grid from '@mui/material/Grid2';

import { 
  AttachedTable,
  Spellbook, 
  UpgradableNumber
} from "@dndModels";
import { CHARACTER_LEVELS, COST_UNIT_OPTIONS, TIME_UNIT_OPTIONS } from "@dndModels/Constants";

import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import CheckBox from "@components/input/CheckBox";
import SelectSpellListBox from "@dndComponents/select/SelectSpellListBox";
import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";

interface Props {
  obj: Spellbook;
  onChange: (changed: Spellbook) => void; 
  disabled: boolean;
  attached_table?: AttachedTable;
}

export default function SpellbookInput({
  obj,
  onChange,
  disabled,
  attached_table
}: Props) {
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        size={{
          xs: 4
        }}>
        <UpgradableNumberBox 
          name="Non extra spells known"
          value={obj.spells_known}
          disabled={disabled}
          attached_table={attached_table}
          onChange={(changed: UpgradableNumber) => {
            const new_obj = obj;
            new_obj.spells_known = changed;
            onChange(new_obj);
          }}
        /> 
      </Grid>
      <Grid
        container
        spacing={0}
        direction="row"
        size={{
          xs: 8
        }}>
        <Grid
          size={{
            xs: 6
          }}>
          <CheckBox 
            name="Rituals Only"
            value={obj.rituals_only}
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.rituals_only = changed;
              onChange(new_obj);
            }}
          /> 
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <SelectSpellListBox
            name="Only Spells from List" 
            allow_all
            value={ obj.spell_list } 
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.spell_list = changed;
              onChange(new_obj);
            }} 
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Knowledge Usage Key"
            value={obj.knowledge_usage_key}
            disabled={disabled}
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.knowledge_usage_key = changed;
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Learning Message"
            value={obj.learning_message}
            disabled={disabled}
            multiline
            onBlur={(changed: string) => {
              const new_obj = obj;
              new_obj.learning_message = changed;
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <UpgradableNumberBox
            name="Time per Level (number)"
            value={obj.time_num_per_spell_level}
            disabled={disabled}
            attached_table={attached_table}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.time_num_per_spell_level = changed;
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <SelectStringBox
            name="Time per Level (unit)"
            value={`${obj.time_unit}`}
            options={TIME_UNIT_OPTIONS}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.time_unit = +changed;
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <UpgradableNumberBox
            name="Cost per Level (number)"
            value={obj.cost_per_spell_level}
            disabled={disabled}
            attached_table={attached_table}
            onChange={(changed: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.cost_per_spell_level = changed;
              onChange(new_obj);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <SelectStringBox
            name="Cost per Level (unit)"
            value={`${obj.cost_unit}`}
            options={COST_UNIT_OPTIONS}
            disabled={disabled}
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.cost_unit = +changed;
              onChange(new_obj);
            }}
          />
        </Grid>
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        <SelectStringBox
          name="Can Modify Spells at Level"
          options={["None",...CHARACTER_LEVELS]}
          value={ obj.can_modify_spells_at_level === -1 ? "None" : `${obj.can_modify_spells_at_level}` }
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.can_modify_spells_at_level = changed === "None" ? -1 : +changed;
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        { obj.can_modify_spells_at_level > -1 &&
          <SelectStringBox
            name="Learn Modified Spells at Level"
            options={["None",...CHARACTER_LEVELS]}
            value={ obj.modified_spells_allowed_at_level === -1 ? "None" : `${obj.modified_spells_allowed_at_level}` }
            onChange={(changed: string) => {
              const new_obj = obj;
              new_obj.modified_spells_allowed_at_level = changed === "None" ? -1 : +changed;
              onChange(new_obj);
            }}
          />
        }
      </Grid>
      <Grid
        size={{
          xs: 4
        }}>
        { obj.modified_spells_allowed_at_level > -1 &&
          <CheckBox
            name="Modifications Cause Level Up"
            value={ obj.modifications_cause_level_up }
            onChange={(changed: boolean) => {
              const new_obj = obj;
              new_obj.modifications_cause_level_up = changed;
              onChange(new_obj);
            }}
          />
        }
      </Grid>
    </Grid>
  );
}

